import {
  ApiNotificationResponseDto,
  DeviceType,
  NotificationType,
} from '@birco/rainmesh-api';
import { IconName } from '../../../shared/icons/icon-map';
import { RouteConstants } from '../../../shared/constants/route-constants';
import { TranslateService } from '@ngx-translate/core';
import { getDisplayRuleName } from '../../rules/utils/rule-name.util';
import { getDeviceDetailRoute } from '../../../shared/utils/device-route.util';

export function getNotificationContent(
  notification: ApiNotificationResponseDto | undefined,
  translateService: TranslateService,
): {
  icon?: DeviceType | IconName;
  headline: string;
  text?: string;
  routerLink?: any[];
} {
  if (!notification) return { headline: '' };

  const translate = translateService.instant.bind(translateService);
  const deviceName = notification.device?.name ?? '';
  const actionDeviceName = notification.ruleAction?.device?.name ?? '';
  const ruleName = getDisplayRuleName(notification.rule, translate);
  const ruleActionValue = notification.ruleAction?.value;

  switch (notification.type) {
    case NotificationType.DEVICE_DATA_ACCESS_FAILURE:
      return {
        icon: notification.device?.type,
        headline: translate(
          'notification.content.deviceDataAccessFailure.headline',
        ),
        text: translate('notification.content.deviceDataAccessFailure.text', {
          deviceName,
        }),
        routerLink: getDeviceDetailRoute(notification.device!),
      };
    case NotificationType.DEVICE_ACTION_TRIGGER_SUCCESS:
      return {
        icon: notification.device?.type,
        headline: translate(
          'notification.content.deviceActionTriggerSuccess.headline',
        ),
        text: translate(
          'notification.content.deviceActionTriggerSuccess.text',
          { deviceName },
        ),
        routerLink: getDeviceDetailRoute(notification.device!),
      };
    case NotificationType.DEVICE_ACTION_TRIGGER_FAILURE:
      return {
        icon: notification.device?.type,
        headline: translate(
          'notification.content.deviceActionTriggerFailure.headline',
        ),
        text: translate(
          'notification.content.deviceActionTriggerFailure.text',
          { deviceName },
        ),
        routerLink: getDeviceDetailRoute(notification.device!),
      };
    case NotificationType.RULE_ACTION_EXECUTION_SUCCESS:
      return {
        icon: notification.device?.type ?? 'ic_rain',
        headline: translate(
          'notification.content.ruleActionExecutionSuccess.headline',
          {
            state: getRuleActionStateLabel(ruleActionValue, translateService),
          },
        ),
        text: translate(
          'notification.content.ruleActionExecutionSuccess.text',
          {
            ruleName,
            deviceName: actionDeviceName,
          },
        ),
        routerLink: ['/', RouteConstants.routeRule, notification.rule!.id],
      };
    case NotificationType.RULE_ACTION_EXECUTION_FAILURE:
      return {
        icon: notification.device?.type ?? 'ic_rain',
        headline: translate(
          'notification.content.ruleActionExecutionFailure.headline',
          {
            state: getRuleActionStateLabel(ruleActionValue, translateService),
          },
        ),
        text: translate(
          'notification.content.ruleActionExecutionFailure.text',
          {
            ruleName,
            deviceName: actionDeviceName,
          },
        ),
        routerLink: ['/', RouteConstants.routeRule, notification.rule!.id],
      };
    case NotificationType.RULE_ACTION_REVERSAL_EXECUTION_SUCCESS:
      return {
        icon: notification.device?.type ?? 'ic_rain',
        headline: translate(
          'notification.content.ruleActionReversalExecutionSuccess.headline',
          {
            state: getRuleActionStateLabel(
              ruleActionValue === undefined ? undefined : !ruleActionValue,
              translateService,
            ),
          },
        ),
        text: translate(
          'notification.content.ruleActionReversalExecutionSuccess.text',
          {
            ruleName,
            deviceName: actionDeviceName,
          },
        ),
        routerLink: ['/', RouteConstants.routeRule, notification.rule!.id],
      };
    case NotificationType.RULE_ACTION_REVERSAL_EXECUTION_FAILURE:
      return {
        icon: notification.device?.type ?? 'ic_rain',
        headline: translate(
          'notification.content.ruleActionReversalExecutionFailure.headline',
          {
            state: getRuleActionStateLabel(
              ruleActionValue === undefined ? undefined : !ruleActionValue,
              translateService,
            ),
          },
        ),
        text: translate(
          'notification.content.ruleActionReversalExecutionFailure.text',
          {
            ruleName,
            deviceName: actionDeviceName,
          },
        ),
        routerLink: ['/', RouteConstants.routeRule, notification.rule!.id],
      };
    case NotificationType.KILL_SWITCH_WAS_TURNED_ON:
      return {
        icon: 'ic_pause',
        headline: translate(
          'notification.content.killSwitchWasTurnedOn.headline',
        ),
        text: translate('notification.content.killSwitchWasTurnedOn.text'),
      };
    case NotificationType.KILL_SWITCH_WAS_TURNED_OFF:
      return {
        icon: 'ic_play',
        headline: translate(
          'notification.content.killSwitchWasTurnedOff.headline',
        ),
        text: translate('notification.content.killSwitchWasTurnedOff.text'),
      };
    case NotificationType.KILL_SWITCH_DEVICE_TRIGGER_FAILURE:
      return {
        icon: notification.device?.type,
        headline: translate(
          'notification.content.killSwitchDeviceTriggerFailure.headline',
        ),
        text: translate(
          'notification.content.killSwitchDeviceTriggerFailure.text',
          { deviceName },
        ),
        routerLink: getDeviceDetailRoute(notification.device!),
      };
    case NotificationType.EMERGENCY_STOP_WAS_TRIGGERED:
      return {
        icon: 'ic_emergency',
        headline: translate(
          'notification.content.emergencyStopWasTriggered.headline',
        ),
        text: translate('notification.content.emergencyStopWasTriggered.text'),
      };
    case NotificationType.EMERGENCY_STOP_WAS_RESET:
      return {
        icon: 'ic_emergency',
        headline: translate(
          'notification.content.emergencyStopWasReset.headline',
        ),
        text: translate('notification.content.emergencyStopWasReset.text'),
      };
    case NotificationType.EMERGENCY_STOP_DEVICE_TRIGGER_FAILURE:
      return {
        icon: notification.device?.type,
        headline: translate(
          'notification.content.emergencyStopDeviceTriggerFailure.headline',
        ),
        text: translate(
          'notification.content.emergencyStopDeviceTriggerFailure.text',
          { deviceName },
        ),
        routerLink: getDeviceDetailRoute(notification.device!),
      };
    case NotificationType.DEVICE_SENSOR_DATA_STALE:
      return {
        icon: notification.device?.type ?? 'ic_rain',
        headline: translate(
          'notification.content.deviceSensorDataStale.headline',
        ),
        text: translate('notification.content.deviceSensorDataStale.text', {
          deviceName,
        }),
        routerLink: getDeviceDetailRoute(notification.device!),
      };
    case NotificationType.CISTERN_OVERFLOW_PROTECTION_AUTOMATED_ACTION_SUCCESS:
      return {
        icon: 'ic_cistern',
        headline: translate(
          'notification.content.cisternOverflowProtectionAutomatedActionSuccess.headline',
        ),
        text: translate(
          'notification.content.cisternOverflowProtectionAutomatedActionSuccess.text',
        ),
      };
    case NotificationType.CISTERN_OVERFLOW_PROTECTION_AUTOMATED_ACTION_FAILURE:
      return {
        icon: 'ic_cistern',
        headline: translate(
          'notification.content.cisternOverflowProtectionAutomatedActionFailure.headline',
        ),
        text: translate(
          'notification.content.cisternOverflowProtectionAutomatedActionFailure.text',
        ),
      };
    case NotificationType.DEVICE_MARKED_OUT_OF_SERVICE:
      return {
        icon: 'ic_cistern',
        headline: translate(
          'notification.content.deviceMarkedOutOfService.headline',
        ),
        text: translate('notification.content.deviceMarkedOutOfService.text', {
          deviceName,
        }),
      };
    case NotificationType.DEVICE_MARKED_IN_SERVICE:
      return {
        icon: 'ic_cistern',
        headline: translate(
          'notification.content.deviceMarkedInService.headline',
        ),
        text: translate('notification.content.deviceMarkedInService.text', {
          deviceName,
        }),
      };
    case NotificationType.SYSTEM_RULE_CREATED:
      return {
        icon: 'ic_rule',
        headline: translate('notification.content.systemRuleCreated.headline'),
        text: translate('notification.content.systemRuleCreated.text', {
          ruleName,
        }),
      };
    default:
      return {
        headline: '',
      };
  }
}

function getRuleActionStateLabel(
  value: boolean | undefined,
  translateService: TranslateService,
): string {
  if (value === undefined) return '';
  return translateService.instant(
    value
      ? 'notification.content.state.activated'
      : 'notification.content.state.deactivated',
  );
}
